import type { Options } from "ky";

import { loginPath } from "#src/router/extra-info";
import { useAuthStore } from "#src/store/auth";
import ky from "ky";

import { AUTH_HEADER, REFRESH_TOKEN_PATH } from "./constants";
import { handleErrorResponse } from "./error-response";
import { globalProgress } from "./global-progress";
import { goLogin } from "./go-login";
import { refreshTokenAndRetry } from "./refresh";

// Request whitelist. APIs in the whitelist do not need to include a token.
const requestWhiteList = [loginPath];

// Request timeout duration
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 10000;

const defaultConfig: Options = {
	// The input argument cannot start with a slash / when using prefixUrl option.
	prefixUrl: import.meta.env.VITE_API_BASE_URL,
	timeout: API_TIMEOUT,
	// Do not set Content-Type globally — FormData needs multipart boundary from the browser.
	// JSON callers use ky `{ json: ... }` which sets application/json.
	retry: {
		// Maximum number of retries when the request fails
		limit: 2,
		delay: (attemptCount: number) => {
			return attemptCount * 500;
		},
		statusCodes: [],
	},
	hooks: {
		beforeRequest: [
			(request, options) => {
				const ignoreLoading = options.ignoreLoading;
				if (!ignoreLoading) {
					globalProgress.start();
				}

				// Requests that do not need to carry a token
				const isWhiteRequest = requestWhiteList.some(url => request.url.endsWith(url));
				if (!isWhiteRequest) {
					const { token } = useAuthStore.getState();
					request.headers.set(AUTH_HEADER, `Bearer ${token}`);
				}

				if (options.body instanceof FormData) {
					// Rebuild Request without Content-Type so fetch sets multipart/form-data; boundary=...
					const headers = new Headers(request.headers);
					headers.delete("Content-Type");
					return new Request(request, {body: options.body, headers});
				}

				// The language header, and others, must be included in all requests
				// request.headers.set(LANG_HEADER, usePreferencesStore.getState().language);
			},
		],
		afterResponse: [
			async (request, options, response) => {
				const ignoreLoading = options.ignoreLoading;
				if (!ignoreLoading) {
					globalProgress.done();
				}
				// request error
				if (!response.ok) {
					if (response.status === 401) {
						// Prevents an infinite loop caused by continuously receiving 401 errors after a refresh token attempt.
						if ([`/${REFRESH_TOKEN_PATH}`].some(url => request.url.endsWith(url))) {
							goLogin();
							return response;
						}
						// If the token is expired, refresh it and try again.
						const { refreshToken } = useAuthStore.getState();
						// If there is no refresh token, it means that the user has not logged in.
						if (!refreshToken) {
							// If the page route has already redirected to the login page, no further redirection is needed, just return the response.
							if (location.pathname === loginPath) {
								return response;
							} else {
								goLogin();
								return response;
							}
						}

						return refreshTokenAndRetry(request, options, refreshToken);
					} else {
						return handleErrorResponse(response);
					}
				}
				// request success
				return response;
			},
		],
	},
};

export const request = ky.create(defaultConfig);
